
import { useState, useRef } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import toast, { Toaster } from "react-hot-toast";
import { AnimatedText } from "../ui/animated-text";
import { GlowyCard } from "../ui/glowy-card";
import { ShinyButton } from "../ui/shiny-button";
import { cn } from "@/lib/utils";

const MIN_MESSAGE_LENGTH = 20;

export function ContactForm() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        message: "",
        cat: "", // Honeypot field
    });

    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const confirmationRef = useRef<HTMLDivElement>(null);

    const validateEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const validateMessage = (message: string): boolean => {
        return message.trim().length >= MIN_MESSAGE_LENGTH;
    };

    const isFormValid = (): boolean => {
        return (
            form.name.trim() !== "" &&
            form.email.trim() !== "" &&
            validateEmail(form.email) &&
            validateMessage(form.message)
        );
    };

    const handleChange = (field: string, value: string) => {
        setForm((prev) => ({ ...prev, [field]: value }));

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }

        // Real-time validation
        if (field === "email" && value.trim() !== "") {
            if (!validateEmail(value)) {
                setErrors((prev) => ({ ...prev, email: "Bitte geben Sie eine gültige E-Mail-Adresse ein" }));
            }
        }

        if (field === "message" && value.trim() !== "") {
            if (!validateMessage(value)) {
                setErrors((prev) => ({
                    ...prev,
                    message: `Die Nachricht muss mindestens ${MIN_MESSAGE_LENGTH} Zeichen lang sein`,
                }));
            }
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Check honeypot - silent reject if filled
        if (form.cat.length > 0) {
            return;
        }

        // Validate message length
        if (form.message.length < MIN_MESSAGE_LENGTH) {
            toast.error(`Die Nachricht muss mindestens ${MIN_MESSAGE_LENGTH} Zeichen lang sein`);
            setErrors((prev) => ({
                ...prev,
                message: `Die Nachricht muss mindestens ${MIN_MESSAGE_LENGTH} Zeichen lang sein`,
            }));
            return;
        }

        // Validate email
        if (!validateEmail(form.email)) {
            toast.error("Bitte geben Sie eine gültige E-Mail-Adresse ein");
            setErrors((prev) => ({ ...prev, email: "Bitte geben Sie eine gültige E-Mail-Adresse ein" }));
            return;
        }

        setIsSubmitting(true);

        try {
            // Prepare form data
            const formData: { name: string; email: string; phone?: string; message: string } = {
                name: form.name.trim(),
                email: form.email.trim(),
                message: form.message.trim(),
            };

            // Append phone to message if provided
            if (form.phone.trim()) {
                formData.message = `${formData.message}\n\nTelefon: ${form.phone.trim()}`;
            }

            // Append fleet size to message if provided
            if (form.fleetSize) {
                formData.message = `${formData.message}\n\nFlottengröße: ${form.fleetSize}`;
            }

            // Get API base URL from environment variable
            // Bun uses import.meta.env in client-side code, but we need to handle it safely
            let apiBaseUrl = "https://api.app.taxifusion.de";
            try {
                if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_BASE_URL) {
                    apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
                }
            } catch (e) {
                // Fallback to default if env access fails
                console.warn("Could not access environment variable, using default API URL");
            }

            // POST to API
            await axios.post(`${apiBaseUrl}/contact`, formData);

            // Success
            setIsSubmitted(true);
            toast.success("Nachricht erfolgreich gesendet! Wir melden uns bald bei Ihnen.");

            // Scroll to confirmation
            setTimeout(() => {
                confirmationRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
            }, 100);

            // Reset form
            setForm({
                name: "",
                email: "",
                phone: "",
                fleetSize: "",
                message: "",
                cat: "",
            });
        } catch (error) {
            console.error("Form submission error:", error);
            toast.error(
                "Fehler beim Senden der Nachricht. Bitte versuchen Sie es später erneut oder kontaktieren Sie uns direkt."
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const messageLength = form.message.length;
    const messageProgress = Math.min((messageLength / MIN_MESSAGE_LENGTH) * 100, 100);
    const isValid = isFormValid();

    return (
        <section id="contact" className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 overflow-hidden">
            <div className="container mx-auto max-w-4xl">
                <div className="mb-8 sm:mb-12 md:mb-16 text-left sm:text-center">
                    <AnimatedText
                        lines={["Kontaktieren Sie uns"]}
                        className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6"
                        direction="up"
                        as="h2"
                    />
                    <p className="text-base sm:text-lg md:text-xl text-gray-400 max-w-2xl mx-auto sm:mx-auto px-2 text-balance">
                        Haben Sie Fragen zur TBOX? Wir helfen Ihnen gerne weiter.
                    </p>
                </div>

                <GlowyCard>
                    {!isSubmitted ? (
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* Honeypot field - hidden from users */}
                            <input
                                type="text"
                                name="cat"
                                value={form.cat}
                                onChange={(e) => handleChange("cat", e.target.value)}
                                className="absolute opacity-0 pointer-events-none h-0 w-0"
                                tabIndex={-1}
                                autoComplete="off"
                                aria-hidden="true"
                            />

                            {/* Name field */}
                            <div>
                                <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                                    Name <span className="text-red-400">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="name"
                                    required
                                    value={form.name}
                                    onChange={(e) => handleChange("name", e.target.value)}
                                    className={cn(
                                        "w-full px-4 py-3 rounded-xl border bg-white/5 text-white placeholder-gray-500",
                                        "focus:outline-none focus:ring-2 focus:ring-white/20 focus:border-white/20",
                                        "transition-all duration-200",
                                        errors.name ? "border-red-500/50" : "border-white/10"
                                    )}
                                    placeholder="Ihr vollständiger Name"
                                />
                                {errors.name && (
                                    <p className="mt-1 text-sm text-red-400">{errors.name}</p>
                                )}
                            </div>

                            {/* Email field */}
                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                                    E-Mail <span className="text-red-400">*</span>
                                </label>
                                <input
                                    type="email"
                                    id="email"
                                    required
                                    value={form.email}
                                    onChange={(e) => handleChange("email", e.target.value)}
                                    className={cn(
                                        "w-full px-4 py-3 rounded-xl border bg-white/5 text-white placeholder-gray-500",
                                        "focus:outline-none focus:ring-2 focus:ring-white/20 focus:border-white/20",
                                        "transition-all duration-200",
                                        errors.email ? "border-red-500/50" : "border-white/10"
                                    )}
                                    placeholder="ihre.email@beispiel.de"
                                />
                                {errors.email && (
                                    <p className="mt-1 text-sm text-red-400">{errors.email}</p>
                                )}
                            </div>

                            {/* Phone field  */}
                            <div>
                                <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2">
                                    Telefon <span className="text-gray-500 text-xs"></span>
                                </label>
                                <input
                                    type="tel"
                                    id="phone"
                                    value={form.phone}
                                    onChange={(e) => handleChange("phone", e.target.value)}
                                    className={cn(
                                        "w-full px-4 py-3 rounded-xl border bg-white/5 text-white placeholder-gray-500",
                                        "focus:outline-none focus:ring-2 focus:ring-white/20 focus:border-white/20",
                                        "transition-all duration-200 border-white/10"
                                    )}
                                    placeholder="030 12345678"
                                />
                            </div>

                            {/* Fleet Size field */}
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-3">
                                    Flottengröße <span className="text-gray-500 text-xs"></span>
                                </label>
                                <div className="flex flex-wrap gap-2">
                                    {[
                                        "1 Fahrzeug",
                                        "1-5 Fahrzeuge",
                                        "6-10 Fahrzeuge",
                                        "11-20 Fahrzeuge",
                                        "21-50 Fahrzeuge",
                                        "50+ Fahrzeuge",
                                    ].map((option) => (
                                        <button
                                            key={option}
                                            type="button"
                                            onClick={() => handleChange("fleetSize", option)}
                                            className={cn(
                                                "px-4 py-2 rounded-full text-sm font-medium transition-all duration-200",
                                                "border focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-black",
                                                "hover:scale-105 active:scale-95",
                                                form.fleetSize === option
                                                    ? "bg-white text-black border-white shadow-lg shadow-white/20"
                                                    : "bg-white/5 text-gray-300 border-white/10 hover:bg-white/10 hover:border-white/20 focus:ring-white/30"
                                            )}
                                        >
                                            {option}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Message field */}
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <label htmlFor="message" className="block text-sm font-medium text-gray-300">
                                        Nachricht <span className="text-red-400">*</span>
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <span
                                            className={cn(
                                                "text-xs font-medium transition-colors",
                                                messageLength >= MIN_MESSAGE_LENGTH
                                                    ? "text-green-400"
                                                    : "text-gray-500"
                                            )}
                                        >
                                            {messageLength} / {MIN_MESSAGE_LENGTH}
                                        </span>
                                    </div>
                                </div>
                                <textarea
                                    id="message"
                                    required
                                    rows={6}
                                    value={form.message}
                                    onChange={(e) => handleChange("message", e.target.value)}
                                    className={cn(
                                        "w-full px-4 py-3 rounded-xl border bg-white/5 text-white placeholder-gray-500",
                                        "focus:outline-none focus:ring-2 focus:ring-white/20 focus:border-white/20",
                                        "transition-all duration-200 resize-none",
                                        errors.message ? "border-red-500/50" : "border-white/10"
                                    )}
                                    placeholder="Ihre Nachricht (mindestens 20 Zeichen)..."
                                />
                                {errors.message && (
                                    <p className="mt-1 text-sm text-red-400">{errors.message}</p>
                                )}

                                {/* Progress indicator */}
                                <div className="mt-2">
                                    <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                                        <motion.div
                                            className={cn(
                                                "h-full rounded-full transition-colors",
                                                messageProgress >= 100
                                                    ? "bg-green-500"
                                                    : messageProgress >= 50
                                                      ? "bg-yellow-500"
                                                      : "bg-gray-500"
                                            )}
                                            initial={{ width: 0 }}
                                            animate={{ width: `${messageProgress}%` }}
                                            transition={{ duration: 0.3 }}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Submit button */}
                            <div className="pt-4">
                                <ShinyButton
                                    type="submit"
                                    glow={isValid}
                                    disabled={!isValid || isSubmitting}
                                    className={cn(
                                        "w-full sm:w-auto min-h-[44px]",
                                        (!isValid || isSubmitting) && "opacity-50 cursor-not-allowed"
                                    )}
                                >
                                    {isSubmitting ? "Wird gesendet..." : "Nachricht senden"}
                                </ShinyButton>
                            </div>
                        </form>
                    ) : (
                        <motion.div
                            ref={confirmationRef}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.5 }}
                            className="text-center py-8"
                        >
                            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500/20 border border-green-500/50 mb-6">
                                <svg
                                    className="w-8 h-8 text-green-400"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M5 13l4 4L19 7"
                                    />
                                </svg>
                            </div>
                            <h3 className="text-2xl font-bold text-white mb-2">Nachricht gesendet!</h3>
                            <p className="text-gray-400">
                                Vielen Dank für Ihre Nachricht. Wir melden uns in Kürze bei Ihnen.
                            </p>
                            <button
                                onClick={() => setIsSubmitted(false)}
                                className="mt-6 text-sm text-gray-400 hover:text-white transition-colors underline"
                            >
                                Weitere Nachricht senden
                            </button>
                        </motion.div>
                    )}
                </GlowyCard>
            </div>

            {/* Toast notifications */}
            <Toaster
                position="top-center"
                toastOptions={{
                    duration: 4000,
                    style: {
                        background: "#1a1a1a",
                        color: "#fff",
                        border: "1px solid rgba(255, 255, 255, 0.1)",
                        borderRadius: "12px",
                    },
                    success: {
                        iconTheme: {
                            primary: "#10b981",
                            secondary: "#fff",
                        },
                    },
                    error: {
                        iconTheme: {
                            primary: "#ef4444",
                            secondary: "#fff",
                        },
                    },
                }}
            />
        </section>
    );
}


